(() => {
  const parseJson = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const asArray = (value) => {
    const parsed = parseJson(value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.bundle_rules)) return parsed.bundle_rules;
    if (parsed && Array.isArray(parsed.rules)) return parsed.rules;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
  };

  const numericId = (value) => {
    if (value == null || value === '') return 0;
    const text = String(value);
    const match = text.match(/(\d{6,})/);
    return match ? Number(match[1]) : Number(text) || 0;
  };

  const productIdsFromRule = (rule) => {
    const ids = new Set();
    const buckets = [rule?.products, rule?.variants, rule?.productIds, rule?.product_ids];
    buckets.forEach((bucket) => {
      const items = asArray(bucket);
      items.forEach((item) => {
        if (item == null) return;
        if (typeof item === 'number' || typeof item === 'string') {
          const id = numericId(item);
          if (id) ids.add(id);
          return;
        }
        const id = numericId(
          item.productId ||
            item.product_id ||
            item.shopifyProductId ||
            item.shopify_product_id ||
            item.id
        );
        if (id) ids.add(id);
      });
    });
    return ids;
  };

  const parentIdFromRule = (rule) =>
    numericId(
      rule?.parentProductId ||
        rule?.parent_product_id ||
        rule?.shopifyProductId ||
        rule?.shopify_product_id ||
        rule?.productId ||
        rule?.product_id ||
        rule?.themeProductId ||
        rule?.theme_product_id
    );

  const matchRule = (rules, uniqueRef, productId, productHandle) => {
    const ref = String(uniqueRef || '').trim();
    const pid = numericId(productId);
    const handle = String(productHandle || '').trim();
    if (ref && ref !== 'from_product') {
      return (
        rules.find(
          (rule) => String(rule.uniqueRef || rule.unique_ref || '').trim() === ref
        ) || null
      );
    }
    const bySource = rules.find((rule) =>
      asArray(rule.singleProductSettings).some((block) => {
        const source = (block && block.sourceProduct) || {};
        return (
          (pid && numericId(source.productId) === pid) ||
          (handle && String(source.handle || '').trim() === handle)
        );
      })
    );
    if (bySource) return bySource;
    if (pid) {
      const byParent = rules.find((rule) => parentIdFromRule(rule) === pid);
      if (byParent) return byParent;
      const byProduct = rules.find((rule) => productIdsFromRule(rule).has(pid));
      if (byProduct) return byProduct;
    }
    return null;
  };

  const variantMatchesOptions = (variant, options) => {
    if (!variant || !Array.isArray(variant.options) || !Array.isArray(options)) return false;
    if (variant.options.length !== options.length) {
      return options.every((value) => variant.options.includes(value));
    }
    return variant.options.every((value, index) => value === options[index]);
  };

  const pickVariant = (product, selectedOptions) => {
    const variants = product.variants || [];
    if (product.lockedVariantId) {
      const locked = variants.find((variant) => Number(variant.id) === Number(product.lockedVariantId));
      if (locked) return locked;
    }
    const available = variants.filter((variant) => variant.available !== false);
    const pool = available.length ? available : variants;
    if (!pool.length) return null;
    if (selectedOptions && selectedOptions.length) {
      const matched = pool.find((variant) => variantMatchesOptions(variant, selectedOptions));
      if (matched) return matched;
    }
    return pool[0];
  };

  const selectedBundleOptions = (root, product) => {
    const fromPicker = [];
    root.querySelectorAll('[data-bundle-option-group]').forEach((group) => {
      const checked = group.querySelector('[data-bundle-option]:checked');
      if (checked) fromPicker.push(checked.value);
    });
    if (fromPicker.length) return fromPicker;

    const productRoot = root.closest('[data-product-root]') || document;
    const fromNative = [];
    productRoot.querySelectorAll('[data-single-option-selector]').forEach((selector) => {
      if (selector.tagName === 'SELECT') {
        if (selector.value) fromNative.push(selector.value);
        return;
      }
      if (selector.checked && selector.value) fromNative.push(selector.value);
    });
    if (fromNative.length) return fromNative;
    return product.options || [];
  };

  const formatMoney = (cents, formatString) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, formatString || (window.theme && window.theme.moneyFormat));
    }

    let value = Number(cents || 0);
    if (typeof cents === 'string') {
      value = Number(String(cents).replace('.', ''));
    }

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    };

    const format = formatString || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const match = String(format).match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    let amount = formatWithDelimiters(value, 2);
    if (placeholder === 'amount_no_decimals') amount = formatWithDelimiters(value, 0);
    if (placeholder === 'amount_with_comma_separator') amount = formatWithDelimiters(value, 2, '.', ',');
    if (placeholder === 'amount_no_decimals_with_comma_separator') amount = formatWithDelimiters(value, 0, '.', ',');
    return String(format).replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  const catalogFromRule = (rule) => {
    const items = asArray(rule && rule.products);
    return items
      .map((item) => {
        if (item == null) return null;
        if (typeof item === 'number' || typeof item === 'string') {
          return {
            id: numericId(item),
            handle: '',
            title: '',
            variants: [{ id: numericId(item), title: '', available: true, options: [] }],
            quantity: 1,
            hasOnlyDefaultVariant: true,
          };
        }
        const variantItems = asArray(item.variants);
        const variants = variantItems.length
          ? variantItems.map((variant) => {
              if (typeof variant === 'number' || typeof variant === 'string') {
                return { id: numericId(variant), title: '', available: true, price: 0, options: [] };
              }
              return {
                id: numericId(variant.variantId || variant.id),
                title: variant.name || variant.title || '',
                available: variant.available !== false,
                price: variant.price,
                options: variant.options || [],
              };
            })
          : [
              {
                id: numericId(item.variantId || item.variant_id),
                title: '',
                available: true,
                price: item.price,
                options: [],
              },
            ];
        return {
          id: numericId(item.productId || item.product_id || item.id),
          handle: item.productHandle || item.handle || '',
          title: item.name || item.title || '',
          image: item.imageSrc || item.image || '',
          url: item.productHandle ? `/products/${item.productHandle}` : '',
          quantity: Number(item.minQuantity || item.quantity || 1) || 1,
          hasOnlyDefaultVariant: variants.length <= 1,
          variants: variants.filter((variant) => variant.id),
        };
      })
      .filter(Boolean);
  };

  const catalogFromSettingsItem = (item) => {
    if (!item) return null;
    const variantId = numericId(item.variantId || (item.type === 'VARIANT' ? item.id : 0));
    const productId = numericId(item.productId || (item.type === 'PRODUCT' ? item.id : 0));
    return {
      id: productId || variantId,
      handle: item.productHandle || item.handle || '',
      title: item.title || item.name || '',
      image: item.imageSrc || item.image || '',
      url: item.productHandle ? `/products/${item.productHandle}` : '',
      quantity: Number(item.quantity || 1) || 1,
      lockedVariantId: variantId || 0,
      hasOnlyDefaultVariant: true,
      variants: variantId
        ? [{ id: variantId, title: item.title || '', available: true, price: 0, options: [] }]
        : [],
    };
  };

  const catalogFromBlock = (block) =>
    asArray(block && block.products).map(catalogFromSettingsItem).filter(Boolean);

  const productJsonCache = new Map();
  const productJsonInflight = new Map();

  const fetchProductJson = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (productJsonCache.has(handle)) return Promise.resolve(productJsonCache.get(handle));
    if (productJsonInflight.has(handle)) return productJsonInflight.get(handle);
    const request = fetch(`/products/${handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data) productJsonCache.set(handle, data);
        productJsonInflight.delete(handle);
        return data;
      })
      .catch(() => {
        productJsonInflight.delete(handle);
        return null;
      });
    productJsonInflight.set(handle, request);
    return request;
  };

  const hydrateItems = async (items) =>
    Promise.all(
      (items || []).map(async (item) => {
        if (!item || !item.handle) return item;
        const data = await fetchProductJson(item.handle);
        return data ? mergeShopifyProduct(item, data) : item;
      })
    );

  const voltageLabel = (block) => {
    const title = String((block && block.sourceProduct && block.sourceProduct.title) || '');
    const match = title.match(/(\d+\s*v)/i);
    if (match) return match[1].toUpperCase().replace(/\s+/g, '');
    return title.replace(/[-–]\s*$/, '').trim() || 'Option';
  };

  const sizedCdnUrl = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const mergeShopifyProduct = (item, data) => {
    const variants = (data.variants || []).map((variant) => ({
      id: variant.id,
      title: variant.title,
      available: variant.available !== false,
      price: variant.price,
      options: variant.options || [],
    }));
    return {
      ...item,
      id: data.id || item.id,
      handle: data.handle || item.handle,
      title: data.title || item.title,
      url: `/products/${data.handle || item.handle}`,
      image: data.featured_image || item.image,
      hasOnlyDefaultVariant: variants.length <= 1,
      options: (data.options || []).map((option) => (typeof option === 'string' ? option : option.name)),
      optionValues: (data.options || []).map((option, index) => {
        if (option && option.values) return option;
        const name = typeof option === 'string' ? option : option.name;
        const values = [
          ...new Set(
            (data.variants || [])
              .map((variant) => (variant.options && variant.options[index]) || variant[`option${index + 1}`])
              .filter(Boolean)
          ),
        ];
        return { name, values };
      }),
      variants,
      tags: data.tags || item.tags || '',
    };
  };

  const isCatalogHidden = (item) => {
    const tags = item && item.tags;
    const list = Array.isArray(tags)
      ? tags
      : String(tags || '')
          .split(',')
          .map((tag) => tag.trim());
    return list.some((tag) => tag.toLowerCase() === 'catalog_hidden');
  };

  const init = (root) => {
    if (!root || root.dataset.bundlePdpReady === 'true') return;
    root.dataset.bundlePdpReady = 'true';

    const config = parseJson(root.querySelector('[data-bundle-pdp-config]')?.textContent) || {};
    const products = Array.isArray(config.products) ? config.products : [];
    const rules = asArray(config.rules);
    const rule = matchRule(
      rules,
      config.uniqueRef,
      config.productId,
      config.parentProduct && config.parentProduct.handle
    );
    const uniqueRef = String(
      (rule && (rule.uniqueRef || rule.unique_ref)) || config.uniqueRef || ''
    ).trim();
    const bundleType = String(
      (rule && rule.bundleType) || config.bundleType || 'SINGLE_PRODUCT_BUILD_A_BOX'
    );
    const bundleName = String((rule && (rule.name || rule.checkoutDiscountName)) || config.name || '');

    const allowedIds = productIdsFromRule(rule);
    const filtered =
      allowedIds.size > 0
        ? products.filter((item) => allowedIds.has(Number(item.id)))
        : products;
    const sourceBlocks = asArray(rule && rule.singleProductSettings);
    let selectedBlock = 0;
    let parentShopify = null;
    const blockCatalogs = sourceBlocks.map((block) => catalogFromBlock(block));
    const blockHydrated = sourceBlocks.map(() => null);
    const blockHydratePromises = sourceBlocks.map(() => null);
    let catalog = blockCatalogs[selectedBlock] ? blockCatalogs[selectedBlock].slice() : [];
    if (!catalog.length) {
      catalog = filtered.length ? filtered : products;
    }
    if (!catalog.length) {
      catalog = catalogFromRule(rule);
    }

    const selectedParentVariant = () => {
      const parent = config.parentProduct;
      if (!parent || !Array.isArray(parent.variants) || !parent.variants.length) return null;
      const selected = [];
      root.querySelectorAll('[data-bundle-parent-option]:checked').forEach((input) => {
        if (input.value) selected.push(input.value);
      });
      if (selected.length) {
        const matched = parent.variants.find((variant) => variantMatchesOptions(variant, selected));
        if (matched) return matched;
      }
      const fromConfig = parent.variants.find((variant) => Number(variant.id) === Number(config.variantId));
      return fromConfig || parent.variants[0];
    };

    const matchSourceBlockIndex = (variant) => {
      if (!variant || !sourceBlocks.length) return 0;
      const hay = `${variant.title || ''} ${(variant.options || []).join(' ')}`.toLowerCase();
      const compact = hay.replace(/\s+/g, '');
      let best = 0;
      let bestScore = -1;
      sourceBlocks.forEach((block, index) => {
        const source = (block && block.sourceProduct) || {};
        const title = String(source.title || '').toLowerCase();
        const handle = String(source.handle || '').toLowerCase();
        let score = 0;
        if (numericId(source.id) === numericId(variant.id)) score += 100;
        if (config.productId && numericId(source.productId) === numericId(config.productId)) score += 5;
        (variant.options || []).concat(variant.title || '').forEach((opt) => {
          const token = String(opt || '').toLowerCase().trim();
          if (token.length < 2) return;
          if (title.includes(token) || handle.includes(token.replace(/\s+/g, '-'))) score += 10;
        });
        const volt = voltageLabel(block).toLowerCase().replace(/\s+/g, '');
        if (volt && compact.includes(volt)) score += 3;
        if (score > bestScore) {
          bestScore = score;
          best = index;
        }
      });
      return best;
    };

    const selectedSourceProduct = () =>
      (sourceBlocks[selectedBlock] && sourceBlocks[selectedBlock].sourceProduct) || {};

    const parentVariantId = () => {
      const fromProduct = selectedParentVariant();
      if (fromProduct) return numericId(fromProduct.id);
      return numericId(selectedSourceProduct().id || config.variantId);
    };

    const parentPriceCents = () => {
      const fromProduct = selectedParentVariant();
      if (fromProduct) return Number(fromProduct.price || 0);
      const variants = (parentShopify && parentShopify.variants) || [];
      const match =
        variants.find((variant) => Number(variant.id) === parentVariantId()) || variants[0];
      return Number((match && match.price) || (parentShopify && parentShopify.price) || 0);
    };

    const applyRuleDetails = () => {
      const titleEl = root.querySelector('[data-bundle-title]');
      if (titleEl && bundleName && !config.lockContent) {
        titleEl.textContent = bundleName;
      }
      const priceEl = root.querySelector('[data-bundle-price]');
      const priceRow = root.querySelector('[data-bundle-price-row]');
      const rulePrice = Number(rule && rule.price);
      if (priceEl && !priceEl.textContent.trim() && rulePrice > 0) {
        priceEl.textContent = formatMoney(Math.round(rulePrice * 100));
        if (priceRow) priceRow.hidden = false;
      }
    };

    const setHero = (src) => {
      if (!src) return;
      const wrap = root.querySelector('.showroom-fixed-bundle__media .media-style');
      if (!wrap) return;
      let img = wrap.querySelector('.showroom-fixed-bundle__image');
      const placeholder = wrap.querySelector('.showroom-fixed-bundle__placeholder');
      if (!img) {
        img = document.createElement('img');
        img.className = 'showroom-fixed-bundle__image absolute inset-0 h-full w-full object-cover';
        wrap.prepend(img);
      }
      img.src = src;
      img.hidden = false;
      img.removeAttribute('hidden');
      if (placeholder) placeholder.remove();
    };

    const updatePriceFromCatalog = () => {
      const priceEl = root.querySelector('[data-bundle-price]');
      const priceRow = root.querySelector('[data-bundle-price-row]');
      if (!priceEl) return;
      const parentCents = parentPriceCents();
      const total = parentCents
        ? parentCents
        : currentSelections().reduce((sum, entry) => {
            return sum + Number((entry.variant && entry.variant.price) || 0) * Number(entry.quantity || 1);
          }, 0);
      if (!total) return;
      priceEl.textContent = formatMoney(total);
      if (priceRow) priceRow.hidden = false;
    };

    const renderFallbackList = () => {
      const fallback = root.querySelector('[data-bundle-inclusions-fallback]');
      const list = root.querySelector('[data-bundle-list]');
      const ready = catalog.filter((item) => item && item.title);
      if (!fallback || !list || !ready.length) return;
      root.querySelectorAll('.showroom-lab--inclusions:not([data-bundle-inclusions-fallback])').forEach((el) => {
        el.hidden = true;
      });
      fallback.hidden = false;
      list.innerHTML = ready
        .map((item) => {
          const option = '';
          const image = item.image
            ? `<img class="showroom-lab__image" src="${sizedCdnUrl(item.image, 240)}" alt="" width="72" height="72" loading="lazy">`
            : '';
          const hidden = isCatalogHidden(item);
          const href = hidden ? '' : item.url || '';
          const media = hidden
            ? `<span class="showroom-lab__media" aria-hidden="true">${image}</span>`
            : `<a class="showroom-lab__media" href="${href || '#'}" tabindex="-1" aria-hidden="true">${image}</a>`;
          const title = hidden
            ? `<span class="showroom-lab__title">${item.title}</span>`
            : `<a class="showroom-lab__title" href="${href || '#'}">${item.title}</a>`;
          return `<li class="showroom-lab__row showroom-lab__row--plain" data-bundle-row="${item.id}">
            ${media}
            <div class="showroom-lab__content">
              ${title}
              <span class="showroom-lab__option" data-bundle-row-option ${option ? '' : 'hidden'}>${option}</span>
            </div>
          </li>`;
        })
        .join('');
    };

    let optionProduct =
      catalog.find((item) => item.hasOnlyDefaultVariant === false && (item.variants || []).length > 1) ||
      null;

    root.querySelectorAll('[data-bundle-option]').forEach((input) => {
      input.addEventListener('change', () => {
        renderSelections();
      });
    });

    const productRoot = root.closest('[data-product-root]') || document;
    productRoot.addEventListener('change', (event) => {
      if (event.target && event.target.matches('[data-single-option-selector]')) {
        renderSelections();
      }
    });

    function currentSelections() {
      return catalog.map((item) => {
        const options =
          optionProduct && item.id === optionProduct.id
            ? selectedBundleOptions(root, item)
            : selectedBundleOptions(root, item);
        const variant = pickVariant(item, options);
        return { product: item, variant, quantity: Number(item.quantity || 1) };
      });
    }

    function renderSelections() {
      currentSelections().forEach((entry) => {
        const row = root.querySelector(`[data-bundle-row="${entry.product.id}"]`);
        if (!row || !entry.variant) return;
        const optionEl = row.querySelector('[data-bundle-row-option]');
        if (optionEl) {
          const title = entry.variant.title && !/default/i.test(entry.variant.title) ? entry.variant.title : '';
          optionEl.textContent = title;
          optionEl.hidden = !title;
        }
      });
    }

    const addBtn = root.querySelector('[data-bundle-add]');
    const errorEl = root.querySelector('[data-bundle-error]');
    const labelEl = addBtn && addBtn.querySelector('[data-bundle-add-label]');
    const idleLabel = (labelEl && labelEl.textContent) || 'Add bundle to cart';

    const setBusy = (busy, label) => {
      if (!addBtn) return;
      addBtn.disabled = Boolean(busy);
      if (labelEl) labelEl.textContent = label || idleLabel;
    };

    const addToCart = async () => {
      const result = root.getBundleCartItems(1);
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      if (result.error) {
        if (errorEl) {
          errorEl.textContent = result.error;
          errorEl.hidden = false;
        }
        return;
      }
      setBusy(true, 'Adding…');
      try {
        const modalCart = String(window.theme?.settings?.cartType || '').toLowerCase() === 'modal';
        const payload = { items: result.items };
        if (modalCart) {
          payload.sections = 'cart-items,cart-footer,cart-item-count';
          payload.sections_url = window.location.pathname;
        }
        const addUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add bundle to cart.');
        }
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
        if (modalCart) {
          document.body.dispatchEvent(
            new CustomEvent('shapes:cart:afteradditem', {
              bubbles: true,
              detail: { response: data, sourceId: root.id || 'showroom-fixed-bundle' },
            })
          );
        }
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message || 'Could not add bundle to cart.';
          errorEl.hidden = false;
        }
      } finally {
        setBusy(false, idleLabel);
      }
    };

    if (addBtn) {
      addBtn.addEventListener('click', (event) => {
        event.preventDefault();
        addToCart();
      });
    }

    root.getBundleCartItems = function getBundleCartItems(quantity) {
      const qty = Math.max(1, Number(quantity || 1));
      if (!uniqueRef) {
        return { error: 'Missing Appstle unique ref on this bundle.' };
      }
      const selections = currentSelections();
      const missing = selections.find((entry) => !entry.variant);
      if (missing) {
        return { error: `Choose an option for ${missing.product.title}.` };
      }
      const unavailable = selections.find((entry) => entry.variant.available === false);
      if (unavailable) {
        return { error: `${unavailable.product.title} is unavailable.` };
      }

      const includeNames = selections
        .map((entry) => entry.product && entry.product.title)
        .filter(Boolean);
      const parentVariant = selectedParentVariant();
      const optionTitle = String((parentVariant && parentVariant.title) || '').trim();
      let checkoutName = bundleName;
      if (
        optionTitle &&
        !/^default title$/i.test(optionTitle) &&
        checkoutName &&
        !checkoutName.toLowerCase().includes(optionTitle.toLowerCase())
      ) {
        checkoutName = `${checkoutName} · ${optionTitle}`;
      }
      const properties = {
        '_appstle-bb-id': uniqueRef,
        '_appstle_bundles_type': bundleType,
        '__appstle-bb-name': checkoutName,
        '_showroom-bundle-kind': 'fixed',
      };
      if (includeNames.length) {
        properties['_showroom-bundle-includes'] = includeNames.join('|');
        properties.Includes = includeNames.join(', ');
      }

      const parentId = parentVariantId();
      if (parentId && (parentPriceCents() > 0 || config.productId)) {
        return {
          items: [
            {
              id: parentId,
              quantity: qty,
              properties,
            },
          ],
        };
      }

      const items = selections.map((entry) => ({
        id: Number(entry.variant.id),
        quantity: entry.quantity * qty,
        properties,
      }));

      return { items };
    };

    const prefetchImages = (items) => {
      (items || []).forEach((item) => {
        if (!item || !item.image) return;
        const img = new Image();
        img.src = sizedCdnUrl(item.image, 240);
      });
    };

    const showSelectedCatalog = () => {
      const items =
        (blockHydrated[selectedBlock] && blockHydrated[selectedBlock].length
          ? blockHydrated[selectedBlock]
          : blockCatalogs[selectedBlock] && blockCatalogs[selectedBlock].length
            ? blockCatalogs[selectedBlock]
            : catalog);
      if (items && items.length) catalog.splice(0, catalog.length, ...items);
      optionProduct =
        catalog.find((item) => item.hasOnlyDefaultVariant === false && (item.variants || []).length > 1) ||
        optionProduct;
      renderFallbackList();
      renderSelections();
      updatePriceFromCatalog();
    };

    const hydrateBlock = (index) => {
      if (!sourceBlocks.length) return Promise.resolve(catalog);
      if (blockHydrated[index]) return Promise.resolve(blockHydrated[index]);
      if (blockHydratePromises[index]) return blockHydratePromises[index];
      const base =
        (blockCatalogs[index] && blockCatalogs[index].length
          ? blockCatalogs[index]
          : catalogFromBlock(sourceBlocks[index])) || [];
      blockHydratePromises[index] = hydrateItems(base).then((items) => {
        blockHydrated[index] = items;
        prefetchImages(items);
        if (index === selectedBlock) showSelectedCatalog();
        return items;
      });
      return blockHydratePromises[index];
    };

    const parentVariantMediaId = (variant) => {
      if (!variant) return 0;
      if (variant.featured_media && variant.featured_media.id) {
        return numericId(variant.featured_media.id);
      }
      return numericId(variant.featured_media_id);
    };

    const syncGalleryToParentVariant = (variant) => {
      const mediaId = parentVariantMediaId(variant);
      if (!mediaId) return;
      const island = root.querySelector('data-island[src="product"]');
      if (island && window.Alpine && typeof Alpine.$data === 'function') {
        try {
          const data = Alpine.$data(island);
          if (data) {
            data.currentMediaId = mediaId;
            return;
          }
        } catch (error) {
          /* fall through to Splide */
        }
      }
      const splideRoot = root.querySelector('.splide--product');
      if (!splideRoot) return;
      splideRoot.dispatchEvent(
        new CustomEvent('shapes:product:variantchange', {
          bubbles: true,
          detail: { variant: { featured_media: { id: mediaId } } },
        })
      );
    };

    const applyParentSelection = () => {
      const variant = selectedParentVariant();
      if (!variant) return;
      selectedBlock = matchSourceBlockIndex(variant);
      showSelectedCatalog();
      hydrateBlock(selectedBlock);
      syncGalleryToParentVariant(variant);
    };

    const renderOptionPicker = () => {
      const mount = root.querySelector('[data-bundle-options-mount]');
      if (!mount) return;
      const parent = config.parentProduct;
      if (parent && Array.isArray(parent.optionValues) && parent.optionValues.length && (parent.variants || []).length > 1) {
        if (mount.querySelector('[data-bundle-parent-option]')) {
          applyParentSelection();
          return;
        }
        const current = selectedParentVariant();
        const currentOptions = (current && current.options) || [];
        mount.innerHTML = parent.optionValues
          .map((option, index) => {
            const values = option.values || [];
            const radios = values
              .map((value, valueIndex) => {
                const id = `bundle-parent-${index}-${valueIndex}`;
                const checked = currentOptions[index] ? currentOptions[index] === value : valueIndex === 0;
                return `<div class="variant-input relative break-words showroom-variant-picker__option">
                  <input class="custom-button peer absolute left-0 top-0 h-full w-full cursor-pointer opacity-0" type="radio" id="${id}" name="bundle-parent-${index}" value="${String(value).replace(/"/g, '&quot;')}" data-bundle-parent-option ${checked ? 'checked' : ''}>
                  <label class="showroom-variant-picker__button cursor-pointer" for="${id}"><span>${value}</span></label>
                </div>`;
              })
              .join('');
            return `<fieldset class="product-variant-picker-block showroom-variant-picker__fieldset">
              <div class="showroom-variant-picker__row">
                <legend class="showroom-variant-picker__label">${option.name}</legend>
                <div class="showroom-variant-picker__options">${radios}</div>
              </div>
            </fieldset>`;
          })
          .join('');
        mount.querySelectorAll('[data-bundle-parent-option]').forEach((input) => {
          input.addEventListener('change', () => {
            applyParentSelection();
          });
        });
        applyParentSelection();
        return;
      }
      if (sourceBlocks.length > 1) {
        if (mount.querySelector('[data-bundle-source]')) return;
        mount.innerHTML = `<fieldset class="product-variant-picker-block showroom-variant-picker__fieldset">
          <div class="showroom-variant-picker__row">
            <legend class="showroom-variant-picker__label">Options</legend>
            <div class="showroom-variant-picker__options">
              ${sourceBlocks
                .map((block, index) => {
                  const label = voltageLabel(block);
                  const id = `bundle-src-${index}`;
                  return `<div class="variant-input relative break-words showroom-variant-picker__option">
                    <input class="custom-button peer absolute left-0 top-0 h-full w-full cursor-pointer opacity-0" type="radio" id="${id}" name="bundle-source" value="${label}" data-bundle-source="${index}" ${index === selectedBlock ? 'checked' : ''}>
                    <label class="showroom-variant-picker__button cursor-pointer" for="${id}"><span>${label}</span></label>
                  </div>`;
                })
                .join('')}
            </div>
          </div>
        </fieldset>`;
        mount.querySelectorAll('[data-bundle-source]').forEach((input) => {
          input.addEventListener('change', () => {
            selectedBlock = Number(input.getAttribute('data-bundle-source'));
            showSelectedCatalog();
            hydrateBlock(selectedBlock);
          });
        });
        return;
      }
      if (!optionProduct) return;
      const groups = optionProduct.optionValues || [];
      if (!groups.length || root.querySelector('.showroom-lab--inclusions [data-bundle-option-group]')) {
        return;
      }
      mount.innerHTML = groups
        .map((option, index) => {
          const values = option.values || [];
          const radios = values
            .map((value, valueIndex) => {
              const id = `bundle-opt-${index}-${valueIndex}`;
              return `<div class="variant-input relative break-words showroom-variant-picker__option">
                <input class="custom-button peer absolute left-0 top-0 h-full w-full cursor-pointer opacity-0" type="radio" id="${id}" name="bundle-option-${index}" value="${String(value).replace(/"/g, '&quot;')}" data-bundle-option ${valueIndex === 0 ? 'checked' : ''}>
                <label class="showroom-variant-picker__button cursor-pointer" for="${id}"><span>${value}</span></label>
              </div>`;
            })
            .join('');
          return `<fieldset class="product-variant-picker-block showroom-variant-picker__fieldset" data-bundle-option-group>
            <div class="showroom-variant-picker__row">
              <legend class="showroom-variant-picker__label">${option.name}</legend>
              <div class="showroom-variant-picker__options">${radios}</div>
            </div>
          </fieldset>`;
        })
        .join('');
      mount.querySelectorAll('[data-bundle-option]').forEach((input) => {
        input.addEventListener('change', () => renderSelections());
      });
    };

    const hydrateFromShopify = async () => {
      const parentHandle = (config.parentProduct && config.parentProduct.handle) || selectedSourceProduct().handle;
      parentShopify = config.parentProduct || null;
      if (!parentShopify && parentHandle) {
        parentShopify = await fetchProductJson(parentHandle);
      }

      if (sourceBlocks.length) {
        await Promise.all(sourceBlocks.map((_, index) => hydrateBlock(index)));
      } else {
        catalog.splice(0, catalog.length, ...(await hydrateItems(catalog.slice())));
        prefetchImages(catalog);
      }
      showSelectedCatalog();
      const hero = catalog.find((item) => item.image);
      if (hero && !config.lockContent) setHero(hero.image);
      renderOptionPicker();
    };

    applyRuleDetails();
    renderOptionPicker();
    renderSelections();
    hydrateFromShopify();
  };

  const initAll = () => {
    document.querySelectorAll('[data-showroom-bundle-pdp]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
